/**
 * `/login/forgot` — dedicated password-reset request (PRD §8). Same layout as `/employee-login`.
 * The confirmation is shown for every address; whether an account exists is only visible in the
 * audit trail (PLAN.md D-16).
 */

import { useRef, useState, type FormEvent } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Trans, useTranslation } from "react-i18next";
import { Check, Loader2, Mail } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GrantonomyLogo } from "@/components/g-logo";
import { LegalContinueNotice } from "@/components/legal-links";
import { PublicLayout } from "@/components/public-layout";
import { RateLimitNotice } from "@/components/rate-limit-notice";
import { TurnstileField, type TurnstileFieldHandle } from "@/components/turnstile-field";
import { api, ApiError } from "@/lib/api";
import { rateLimitedFromError } from "@/lib/rate-limit";
import { homePathFor, useSession } from "@/lib/session";
import type { RateLimitedPayload } from "@/shared/errors";

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const { user, isLoading } = useSession();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(() => params.get("email")?.trim() ?? "");
  const [sent, setSent] = useState(false);
  const [rateLimit, setRateLimit] = useState<RateLimitedPayload | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const widgetRef = useRef<TurnstileFieldHandle>(null);

  const request = useMutation({
    mutationFn: (input: { email: string; token: string }) =>
      api.post("/auth/password-reset", {
        email: input.email,
        "cf-turnstile-response": input.token,
      }),
    onSuccess: () => setSent(true),
    onError: (error: unknown) => {
      const payload = rateLimitedFromError(error);
      if (payload) {
        setRateLimit(payload);
        return;
      }
      toast.error(
        error instanceof ApiError ? error.localizedMessage : t("forgotPassword.genericError"),
      );
    },
    onSettled: () => widgetRef.current?.reset(),
  });

  if (!isLoading && user) return <Navigate to={homePathFor(user)} replace />;

  function submit(event: FormEvent) {
    event.preventDefault();
    const address = email.trim().toLowerCase();
    if (!address || !token) return;
    setEmail(address);
    request.mutate({ email: address, token });
  }

  return (
    <PublicLayout>
      <div className="flex flex-1 items-center justify-center px-4 py-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="mb-8">
            <GrantonomyLogo />
          </div>
          <h1 className="font-display text-4xl">{t("forgotPassword.title")}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{t("forgotPassword.subtitle")}</p>

          <div className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-soft">
            {sent ? (
              <div className="text-center">
                <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-full bg-yellow">
                  <Check className="size-6" />
                </div>
                <h2 className="text-lg font-medium">{t("forgotPassword.sentTitle")}</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  <Trans
                    i18nKey="forgotPassword.sentBody"
                    values={{ email }}
                    components={{ b: <b /> }}
                  />
                </p>
                <Button variant="ghost" size="sm" className="mt-4" onClick={() => setSent(false)}>
                  {t("forgotPassword.useOtherEmail")}
                </Button>
                <div className="mt-2">
                  <Button asChild variant="outline" size="sm">
                    <Link to="/login">{t("forgotPassword.backToLogin")}</Link>
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                {rateLimit && (
                  <RateLimitNotice payload={rateLimit} onExpired={() => setRateLimit(null)} />
                )}
                <div>
                  <Label className="text-xs" htmlFor="forgot-email">
                    {t("forgotPassword.emailLabel")}
                  </Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder={t("auth.emailPlaceholder")}
                  />
                </div>
                <TurnstileField ref={widgetRef} action="password-reset" onToken={setToken} />
                <Button
                  type="submit"
                  className="w-full"
                  disabled={request.isPending || Boolean(rateLimit) || !email || !token}
                >
                  {request.isPending ? (
                    <>
                      <Loader2 className="mr-2 size-4 animate-spin" />{" "}
                      {t("forgotPassword.submitting")}
                    </>
                  ) : (
                    <>
                      <Mail className="mr-2 size-4" /> {t("forgotPassword.submit")}
                    </>
                  )}
                </Button>
                <LegalContinueNotice className="mt-3" />
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </PublicLayout>
  );
}
